import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../../components/Header';

type AiLog = {
  id: string;
  conversationId: string;
  traceId: string;
  stage: string;
  title: string;
  summary?: string | null;
  payload?: Record<string, unknown> | null;
  createdAt: string;
  conversation?: {
    platform?: string;
    channelType?: string;
    contactName?: string | null;
    contactHandle?: string | null;
    title?: string | null;
    externalThreadId?: string | null;
  } | null;
};

type LogsResponse = { items: AiLog[]; page: number; pageSize: number; total: number };

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function stageTone(stage: string) {
  if (stage.includes('response') || stage.includes('done')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (stage.includes('tool') || stage.includes('search')) return 'bg-cyan-50 text-cyan-700 border-cyan-200';
  if (stage.includes('request') || stage.includes('start')) return 'bg-indigo-50 text-indigo-700 border-indigo-200';
  if (stage.includes('skipped') || stage.includes('empty')) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-50 text-slate-700 border-slate-200';
}

function shortId(value: string) {
  return value ? value.slice(0, 8) : '';
}

function contactLabel(log: AiLog) {
  const c = log.conversation;
  return c?.contactName || c?.contactHandle || c?.title || c?.externalThreadId || log.conversationId;
}

function PayloadViewer({ payload }: { payload?: Record<string, unknown> | null }) {
  const [open, setOpen] = useState(false);
  if (!payload || !Object.keys(payload).length) return null;
  return (
    <div className="mt-3">
      <button type="button" onClick={() => setOpen(!open)} className="text-xs font-medium text-slate-600 hover:text-slate-900">
        {open ? 'Ocultar detalhes completos' : 'Ver entrada/saida completa'}
      </button>
      {open && (
        <pre className="mt-2 max-h-96 overflow-auto rounded-md border border-slate-200 bg-slate-950 p-3 text-xs leading-5 text-slate-100">
          {JSON.stringify(payload, null, 2)}
        </pre>
      )}
    </div>
  );
}

export function LogsPage() {
  const [rows, setRows] = useState<AiLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTrace, setSelectedTrace] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '60' });
      if (search.trim()) params.set('search', search.trim());
      const response = await fetch(`/api/crm-chat/ai-logs?${params.toString()}`, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' } as HeadersInit
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || 'Erro ao carregar logs');
      const data = payload.data as LogsResponse;
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); }, [page]);

  const traces = useMemo(() => {
    const grouped = new Map<string, AiLog[]>();
    rows.forEach((row) => grouped.set(row.traceId, [...(grouped.get(row.traceId) || []), row]));
    return Array.from(grouped.entries()).map(([traceId, items]) => ({
      traceId,
      items: items.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
      first: items[items.length - 1],
      latest: items[0]
    }));
  }, [rows]);

  const currentTrace = traces.find((trace) => trace.traceId === selectedTrace) || traces[0];

  useEffect(() => {
    if (!selectedTrace && traces[0]) setSelectedTrace(traces[0].traceId);
  }, [traces, selectedTrace]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header title="Logs" subtitle="Pipeline completo das respostas automáticas do Chat CRM" />
      <div className="mx-auto flex w-full max-w-7xl gap-4 px-4 py-5">
        <aside className="w-96 shrink-0 rounded-md border border-slate-200 bg-white">
          <div className="border-b border-slate-200 p-3">
            <div className="flex gap-2">
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar conversa, etapa ou texto" className="min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500" />
              <button onClick={() => { setPage(1); loadLogs(); }} className="rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white">Buscar</button>
            </div>
          </div>
          <div className="max-h-[calc(100vh-210px)] overflow-auto">
            {loading ? <div className="p-4 text-sm text-slate-500">Carregando logs...</div> : traces.map((trace) => (
              <button key={trace.traceId} onClick={() => setSelectedTrace(trace.traceId)} className={`block w-full border-b border-slate-100 p-3 text-left hover:bg-slate-50 ${selectedTrace === trace.traceId ? 'bg-slate-100' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-slate-900">{contactLabel(trace.latest)}</span>
                  <span className="text-[11px] text-slate-500">{trace.items.length} etapas</span>
                </div>
                <div className="mt-1 truncate text-xs text-slate-500">{trace.latest.conversation?.platform || 'crm'} · {shortId(trace.traceId)}</div>
                <div className="mt-1 truncate text-xs text-slate-600">{trace.latest.summary || trace.latest.title}</div>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-slate-200 p-3 text-xs text-slate-500">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded border px-2 py-1 disabled:opacity-40">Anterior</button>
            <span>{total} registros</span>
            <button disabled={page * 60 >= total} onClick={() => setPage(page + 1)} className="rounded border px-2 py-1 disabled:opacity-40">Proxima</button>
          </div>
        </aside>

        <section className="min-w-0 flex-1 rounded-md border border-slate-200 bg-white">
          {!currentTrace ? <div className="p-6 text-sm text-slate-500">Nenhum log encontrado.</div> : (
            <div>
              <div className="border-b border-slate-200 p-4">
                <div className="text-sm font-semibold text-slate-900">{contactLabel(currentTrace.latest)}</div>
                <div className="mt-1 text-xs text-slate-500">Trace {currentTrace.traceId} · Conversa {currentTrace.latest.conversationId}</div>
              </div>
              <div className="space-y-0 p-4">
                {currentTrace.items.map((log, index) => (
                  <div key={log.id} className="relative border-l border-slate-200 pb-5 pl-5 last:pb-0">
                    <div className="absolute -left-2 top-0 h-4 w-4 rounded-full border-2 border-white bg-slate-400" />
                    <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-semibold text-slate-400">#{index + 1}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${stageTone(log.stage)}`}>{log.stage}</span>
                        <span className="text-sm font-semibold text-slate-900">{log.title}</span>
                        <span className="ml-auto text-xs text-slate-500">{new Date(log.createdAt).toLocaleString('pt-BR')}</span>
                      </div>
                      {log.summary && <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">{log.summary}</p>}
                      <PayloadViewer payload={log.payload} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
